/**
 * Settings: the kill switch, feature flags, and Connections. SECURITY.md 6 and 7, OPERATIONS.md 7.
 *
 * The kill switch is presented first and prominently, because it is the control that matters
 * when something is going wrong and nobody wants to hunt for it.
 *
 * CONNECTIONS is where a QuickBooks company file is connected, and it is the only screen in the
 * application that goes anywhere near a real credential (plan 10, decision 7).
 *
 * THE CONNECT BUTTON IS THE PATH THAT MATTERS. It asks the API for an Intuit authorize URL and
 * navigates there; the owner signs in at Intuit and approves; Intuit hands the credential to our
 * server, which encrypts it on arrival. The token never exists in this page at all — not in a
 * field, not in state, not in memory. That is strictly better than any amount of care taken with
 * a value a person has copied, because there is no value for a person to copy.
 *
 * THE PASTE FORM REMAINS, labelled as a fallback for when the button cannot reach Intuit, and it
 * is still shaped by the rule it always was: a token exists in this page for as long as it takes
 * to submit it and nowhere else, ever. Each input is `type="password"` with `autocomplete="off"`;
 * the value is read out of the form on submit and never put into React state, a query key, a URL,
 * or `localStorage`; the form is reset the moment the write succeeds; and nothing is echoed back,
 * because `GET /api/credentials` has no field that could carry one.
 *
 * The `?connected=` / `?error=` the callback redirects back with are short non-secret words, read
 * once on arrival and then cleared from the URL so a refresh does not re-show a stale result.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  api,
  type ConnectionsResponse,
  type Entity,
  type FeatureFlag,
  type KillSwitchState,
  type UserJson,
} from '../api.js';

const SCOPES = [
  {
    scope: 'platform_read_only',
    label: 'Return the entire platform to READ-ONLY mode',
    detail: 'Blocks every side-effecting write and every write-back connector.',
    field: 'platformReadOnly' as const,
  },
  {
    scope: 'all_agents',
    label: 'Disable all agents',
    detail: 'No agent run starts. Queued jobs wait rather than being discarded.',
    field: 'allAgentsDisabled' as const,
  },
  {
    scope: 'tool_execution',
    label: 'Disable tool execution',
    detail: 'Agents may still reason and recommend, but no tool runs.',
    field: 'toolExecutionDisabled' as const,
  },
  {
    scope: 'autonomous_actions',
    label: 'Disable autonomous actions',
    detail: 'Level 3 agents cannot execute without approval. On by default until Phase 12.',
    field: 'autonomousActionsDisabled' as const,
  },
];

/** A timestamp, or an em dash. These three times are the whole of what a connection reports. */
function when(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleString() : '—';
}

/**
 * What the callback's `?error=` codes mean, in plain English.
 *
 * The API deliberately redirects with a WORD rather than a message, because a URL is
 * shoulder-surfable, shareable, and kept in browser history, and a message that names a realm or
 * a remedy is a message that could one day name something worse. The sentence is written here,
 * on the screen that shows it; the API's own log holds the detailed error for whoever is
 * debugging.
 */
const CONNECT_ERRORS: Record<string, string> = {
  state:
    'That authorization could not be matched to a request from this application, or it had ' +
    'already been used. Click Connect again to start a fresh one.',
  state_expired:
    'That authorization took longer than ten minutes and has expired. Click Connect again.',
  declined: 'The authorization was cancelled at Intuit. Nothing was changed.',
  no_realm: 'Intuit did not say which company file was approved. Nothing was changed.',
  no_code: 'Intuit did not return an authorization code. Nothing was changed.',
  app_credential:
    'The Intuit application’s client id and secret are not stored, so the authorization could ' +
    'not be completed. Enter them above and try again.',
  entity_realm_conflict:
    'That entity already keeps its books in a different QuickBooks company file. Nothing was ' +
    'changed — sign in to Intuit as the company file this entity actually uses.',
  realm_entity_conflict:
    'That QuickBooks company file is already connected to another entity. One company file ' +
    'belongs to one entity, so nothing was changed.',
  read_only:
    'The platform is in READ-ONLY mode, and storing a credential is a write. Nothing was ' +
    'changed. Turn READ-ONLY off and click Connect again.',
  exchange:
    'Intuit would not complete the exchange. Nothing was stored. The code Intuit issues is ' +
    'single-use and expires within minutes, so click Connect again rather than reloading.',
};

/** The credential forms' field names, as their labels read. */
const FIELD_LABELS: Record<string, string> = {
  clientId: 'Client id',
  clientSecret: 'Client secret',
  keySet: 'Key set',
  accountRef: 'Account',
  refreshToken: 'Refresh token',
  apiKey: 'Key',
};

export function SettingsPage(): ReactNode {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  /** What the last credential write did. Never a value — only which row it landed in. */
  const [stored, setStored] = useState<string | null>(null);
  /**
   * The outcome of the last credential form, shown under THAT form. The page-level alert sits at
   * the top of a long page, out of sight from the Store button, and on 2026-10-29 the owner
   * pressed Store five times without seeing why it refused.
   */
  const [credentialNote, setCredentialNote] = useState<{
    provider: string;
    ok: boolean;
    text: string;
  } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  /** The outcome of a connect round trip, read out of the query on arrival and then cleared. */
  const [connectResult, setConnectResult] = useState<{ ok: boolean; message: string } | null>(null);

  const killSwitch = useQuery({
    queryKey: ['kill-switch'],
    queryFn: () => api.get<{ killSwitch: KillSwitchState }>('/api/kill-switch'),
  });

  const flags = useQuery({
    queryKey: ['feature-flags'],
    queryFn: () => api.get<{ flags: FeatureFlag[] }>('/api/feature-flags'),
  });

  const setSwitch = useMutation({
    mutationFn: (body: { scope: string; enabled: boolean }) => api.post('/api/kill-switch', body),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['kill-switch'] });
      void queryClient.invalidateQueries({ queryKey: ['audit'] });
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : 'Could not change the kill switch.'),
  });

  /**
   * The two set-membership scopes of CLAUDE.md 11. `connector.<name>` flags are what the platform
   * calls its integrations, so the integration list is derived from them rather than kept in a
   * second place that could drift.
   */
  const setTarget = useMutation({
    mutationFn: (body: { kind: 'agent' | 'integration'; target: string; disabled: boolean }) =>
      api.post('/api/kill-switch/target', { ...body, reason: 'Changed in Settings' }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['kill-switch'] });
      void queryClient.invalidateQueries({ queryKey: ['control-center'] });
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : 'Could not change the kill switch.'),
  });

  const setFlag = useMutation({
    mutationFn: (input: { key: string; enabled: boolean }) =>
      api.post(`/api/feature-flags/${input.key}`, { enabled: input.enabled }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
    },
    onError: (err: unknown) =>
      setError(
        err instanceof ApiError
          ? err.killSwitch
            ? `${err.message} Turning a capability ON is blocked in READ-ONLY mode; turning one OFF is always allowed.`
            : err.message
          : 'Could not change the flag.',
      ),
  });

  // ---------------------------------------------------------------------------------------- //
  // Connections (plan 10, Task 4)
  // ---------------------------------------------------------------------------------------- //

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ users: UserJson[] }>('/api/users'),
  });
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<{ user: { id: string } }>('/api/me'),
  }).data?.user;
  const setActive = useMutation({
    mutationFn: (args: { id: string; isActive: boolean }) =>
      api.patch<{ user: UserJson }>(`/api/users/${args.id}`, { isActive: args.isActive }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const syncNow = useMutation({
    mutationFn: () => api.post<{ status: string; accounts: number }>('/api/credentials/sync', {}),
    onSuccess: (result) => {
      setError(null);
      setStored(
        `Sync queued for ${result.accounts} connected account${result.accounts === 1 ? '' : 's'}. ` +
          'The worker picks it up within a minute; "last synced" below moves when it finishes.',
      );
    },
    onError: (err: unknown) => {
      setStored(null);
      setError(err instanceof ApiError ? err.message : 'Could not queue the sync.');
    },
  });

  const entities = useQuery({
    queryKey: ['entities'],
    queryFn: () => api.get<{ entities: Entity[] }>('/api/entities'),
  });

  /**
   * Owner-only, like the write: the list says which company files are connected and when each
   * last synced, and a viewer enumerating the platform's connections is reconnaissance. A 403
   * here is a correct answer, not a failure, so it is not retried.
   */
  const credentials = useQuery({
    queryKey: ['credentials'],
    queryFn: () => api.get<ConnectionsResponse>('/api/credentials'),
    retry: false,
  });

  /**
   * Start the authorization: ask for an Intuit URL, then LEAVE.
   *
   * `window.location.href`, not a fetch that follows a redirect — OAuth needs a top-level
   * navigation, because the owner has to see Intuit's own sign-in and consent screens on
   * Intuit's own origin. That is the property that makes this safe: the password is typed at
   * Intuit, and this application never sees it.
   */
  const connect = useMutation({
    mutationFn: (entityId: string) =>
      api.get<{ url: string }>(
        `/api/credentials/qbo/authorize?entity=${encodeURIComponent(entityId)}`,
      ),
    onSuccess: (result) => {
      window.location.href = result.url;
    },
    onError: (err: unknown) => {
      setConnectResult(null);
      setError(
        err instanceof ApiError ? err.message : 'Could not start the QuickBooks authorization.',
      );
    },
  });

  /**
   * Read the callback's result out of the URL, once, then remove it.
   *
   * Clearing it is not tidiness: without it a refresh re-shows a result for an authorization
   * that happened minutes ago, and the owner cannot tell a stale banner from a fresh one. The
   * query carries only a realm id or one of the words in `CONNECT_ERRORS` — never a token, never
   * a code — because the API put nothing else there.
   */
  useEffect(() => {
    const connected = searchParams.get('connected');
    const failed = searchParams.get('error');
    if (connected === null && failed === null) return;

    setConnectResult(
      connected !== null
        ? { ok: true, message: `Connected QuickBooks company file ${connected}.` }
        : {
            ok: false,
            message:
              CONNECT_ERRORS[failed ?? ''] ??
              'The QuickBooks authorization did not complete. Nothing was stored.',
          },
    );

    const next = new URLSearchParams(searchParams);
    next.delete('connected');
    next.delete('error');
    setSearchParams(next, { replace: true });

    // Both lists change on a successful connect: the credential is new, and the entity may have
    // just learned its realm.
    void queryClient.invalidateQueries({ queryKey: ['credentials'] });
    void queryClient.invalidateQueries({ queryKey: ['entities'] });
    void queryClient.invalidateQueries({ queryKey: ['audit'] });
  }, [searchParams, setSearchParams, queryClient]);

  // Renaming, inline in the Connections table: the row's name becomes a field with Save and
  // Cancel. One entity at a time; the id says which.
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const renameEntity = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api.patch<{ entity: { id: string; name: string } }>(`/api/entities/${input.id}`, {
        name: input.name,
      }),
    onSuccess: (result) => {
      setError(null);
      setStored(`Renamed to ${result.entity.name}.`);
      setRenaming(null);
      void queryClient.invalidateQueries({ queryKey: ['entities'] });
      void queryClient.invalidateQueries({ queryKey: ['audit'] });
    },
    onError: (err: unknown) => {
      setStored(null);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  const createEntity = useMutation({
    mutationFn: (body: Record<string, string>) =>
      api.post<{ entity: { id: string; name: string } }>('/api/entities', body),
    onSuccess: (result) => {
      setError(null);
      setStored(
        `Added ${result.entity.name}. Connect QuickBooks on its row to bind a company file.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['entities'] });
      void queryClient.invalidateQueries({ queryKey: ['audit'] });
    },
    onError: (err: unknown) => {
      setStored(null);
      setError(
        err instanceof ApiError
          ? err.killSwitch
            ? `${err.message} Adding an entity is a write, and READ-ONLY stops writes.`
            : err.message
          : 'Could not add the entity.',
      );
    },
  });

  /**
   * Read the three fields, clear the form, then send — the same shape `submitCredential` uses,
   * minus the reason it clears FIRST: none of these is a secret, so the order is only tidiness.
   */
  function submitEntity(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const fields: Record<string, string> = {};
    for (const [name, value] of data.entries()) fields[name] = String(value).trim();
    if (Object.values(fields).some((value) => value === '')) {
      setStored(null);
      setError('Name, legal name and jurisdiction are all required.');
      return;
    }
    form.reset();
    createEntity.mutate(fields);
  }

  /**
   * Stripe is two writes in one form: bind the account to the entity, then store the key under
   * it. The bind goes first because the credential store refuses a key for an unbound account,
   * and the key is read out of the form ONCE, before the form is cleared, exactly as
   * `submitCredential` does — it never enters React state.
   */
  const bindStripe = useMutation({
    mutationFn: (input: { entityId: string; accountId: string; apiKey: string }) =>
      api
        .post<{ bound: { entityName: string } }>(`/api/entities/${input.entityId}/stripe-account`, {
          accountId: input.accountId,
        })
        .then(() =>
          api.post<{ provider: string; accountRef: string; action: string }>('/api/credentials', {
            provider: 'stripe',
            accountRef: input.accountId,
            apiKey: input.apiKey,
          }),
        ),
    onSuccess: (result) => {
      setError(null);
      setStored(`Stored the Stripe key for ${result.accountRef}.`);
      void queryClient.invalidateQueries({ queryKey: ['credentials'] });
      void queryClient.invalidateQueries({ queryKey: ['entities'] });
      void queryClient.invalidateQueries({ queryKey: ['audit'] });
    },
    onError: (err: unknown) => {
      setStored(null);
      setError(err instanceof ApiError ? err.message : 'Could not connect the Stripe account.');
    },
  });

  function submitStripe(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const entityId = String(data.get('entityId') ?? '');
    const accountId = String(data.get('accountId') ?? '').trim();
    const apiKey = String(data.get('apiKey') ?? '');
    form.reset();
    if (entityId === '' || accountId === '' || apiKey.trim() === '') {
      setStored(null);
      setError('Entity, account id and restricted key are all required.');
      return;
    }
    bindStripe.mutate({ entityId, accountId, apiKey });
  }

  const storeCredential = useMutation({
    mutationFn: (body: Record<string, string>) =>
      api.post<{ provider: string; accountRef: string; action: string }>('/api/credentials', body),
    onSuccess: (result, body) => {
      const text =
        result.action === 'credential.rotate'
          ? `Replaced the stored credential for ${result.provider}/${result.accountRef}.`
          : `Stored the credential for ${result.provider}/${result.accountRef}.`;
      setError(null);
      setStored(text);
      setCredentialNote({ provider: body.provider ?? '', ok: true, text });
      void queryClient.invalidateQueries({ queryKey: ['credentials'] });
      void queryClient.invalidateQueries({ queryKey: ['audit'] });
    },
    onError: (err: unknown, body) => {
      const text =
        err instanceof ApiError
          ? err.killSwitch
            ? `${err.message} Storing a credential is a write, and READ-ONLY stops writes.`
            : err.message
          : 'Could not store the credential.';
      setStored(null);
      setError(text);
      setCredentialNote({ provider: body.provider ?? '', ok: false, text });
    },
  });

  /**
   * Read the value out of the form, clear the form, then send it.
   *
   * The clear comes FIRST and happens whether or not the write succeeds: a token that stays in a
   * field is a token in the browser's memory, in a screenshot, and in whatever the browser
   * decides to restore on a back navigation. Nothing here puts it in React state, so it exists
   * only as an argument on its way to `fetch`.
   */
  function submitCredential(event: FormEvent<HTMLFormElement>, body: Record<string, string>): void {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const fields: Record<string, string> = { ...body };
    for (const [name, value] of data.entries()) fields[name] = String(value);
    form.reset();
    const empty = Object.entries(fields)
      .filter(([, value]) => value.trim() === '')
      .map(([name]) => FIELD_LABELS[name] ?? name);
    if (empty.length > 0) {
      // Name the empty box: a paste of Intuit's "Copy all" lands both values in one box and
      // leaves the other empty, which "every field is required" never said.
      const text =
        `${empty.join(' and ')} ${empty.length === 1 ? 'was' : 'were'} empty, so nothing was ` +
        'stored. Paste each value into its own box, then press Store again.';
      setStored(null);
      setError(text);
      setCredentialNote({ provider: body.provider ?? '', ok: false, text });
      return;
    }
    setCredentialNote(null);
    storeCredential.mutate(fields);
  }

  const state = killSwitch.data?.killSwitch;
  const flagRows = flags.data?.flags ?? [];
  const byPhase = new Map<number, FeatureFlag[]>();
  for (const flag of flagRows) {
    byPhase.set(flag.phase, [...(byPhase.get(flag.phase) ?? []), flag]);
  }

  // ONE ROW PER ENTITY, whether or not it has a realm yet.
  //
  // It used to list only entities that already carried a `qbo_realm_id`, because the realm had to
  // be set by hand before a token could be pasted for it. The connect flow learns the realm from
  // Intuit — the callback is told which company file was approved and records it — so an entity
  // with no realm is no longer a row with nothing to offer; it is the ordinary starting state,
  // and hiding it would hide the button that fixes it.
  const storedCredentials = credentials.data?.credentials ?? [];
  const connections = (entities.data?.entities ?? []).map((entity) => ({
    entity,
    realm: entity.qbo_realm_id,
    credential:
      entity.qbo_realm_id === null
        ? undefined
        : storedCredentials.find(
            (c) => c.provider === 'qbo' && c.accountRef === entity.qbo_realm_id,
          ),
  }));
  const appCredential = storedCredentials.find((c) => c.provider === 'qbo-app');
  const guestyCredential = storedCredentials.find((c) => c.provider === 'guesty');
  // Derived by the API, never typed here: the authorize URL, the token exchange, and what the
  // owner registers on the Intuit app must be the same string or Intuit refuses the flow.
  const redirectUri = credentials.data?.redirectUri;
  const notOwner = credentials.error instanceof ApiError && credentials.error.status === 403;

  return (
    <section>
      <h1>Settings</h1>

      {error && <div className="alert">{error}</div>}

      <div className="card">
        <h2>Kill switch</h2>
        <p className="muted small">
          Checked at job pickup, tool execution, and every connector call. A change takes effect
          within five seconds.
        </p>

        {state?.platformReadOnly && (
          <div className="alert alert-warn">
            The platform is in READ-ONLY mode. Writes are blocked.
            {state.reason ? ` Reason: ${state.reason}` : ''}
          </div>
        )}

        {killSwitch.isLoading && <p className="muted">Loading…</p>}

        {state && (
          <ul className="switches">
            {SCOPES.map((s) => (
              <li key={s.scope}>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={state[s.field]}
                    disabled={setSwitch.isPending}
                    onChange={(e) =>
                      setSwitch.mutate({ scope: s.scope, enabled: e.currentTarget.checked })
                    }
                  />
                  <span>
                    <strong>{s.label}</strong>
                    <span className="muted small block">{s.detail}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card">
        <h2>Stop one integration</h2>
        <p className="muted small">
          Blocks every call to that connector while leaving the rest of the platform running. Per
          agent, the same control is on the Agent Control Center, next to the agent it stops.
        </p>

        {state && (
          <ul className="switches">
            {flagRows
              .filter((f) => f.key.startsWith('connector.'))
              .map((f) => {
                // The connector key inside the flag name: connector.quickbooks_online -> the
                // integration id the runtime checks.
                const target = f.key.slice('connector.'.length).replace(/_/g, '-');
                const stopped = state.disabledIntegrations.includes(target);
                return (
                  <li key={f.key}>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={stopped}
                        disabled={setTarget.isPending}
                        onChange={(e) =>
                          setTarget.mutate({
                            kind: 'integration',
                            target,
                            disabled: e.currentTarget.checked,
                          })
                        }
                      />
                      <span>
                        <strong>Stop {f.description}</strong>
                        <span className="muted small block">
                          {stopped
                            ? 'Every call to this connector is refused.'
                            : f.enabled
                              ? 'Running.'
                              : 'Its feature flag is off, so it is not running anyway.'}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
          </ul>
        )}
      </div>

      {/*
        This section carries no SAMPLE DATA badge of its own, deliberately: it is the one screen in
        the application about REAL credentials, so a badge calling its contents fictional would be
        false, and a badge that lies is worse than none (CLAUDE.md 3.4). The Shell's environment
        badge, which is what the rule actually requires of every screen, still applies here.
      */}
      <div className="card">
        <h2>Connections</h2>
        <p className="muted small">
          One row per entity. Connect signs you in at Intuit and Intuit hands the credential
          straight to this server, which encrypts it on arrival &mdash; no token is ever shown,
          copied, or typed. Nothing stored here can be read back afterwards, not by this screen, not
          by the API, not by anyone working on the system, so a lost credential is re-authorised
          rather than recovered.
        </p>

        {connectResult && (
          <div className={connectResult.ok ? 'alert alert-ok' : 'alert alert-warn'}>
            {connectResult.message}
          </div>
        )}

        {stored && <div className="alert alert-ok">{stored}</div>}

        {notOwner && <p className="muted">Connections are visible to the owner only.</p>}

        {!notOwner && (
          <>
            {redirectUri && (
              <p className="muted small">
                Register this redirect URI on the Intuit app, under Keys &amp; credentials &rarr;
                Redirect URIs. Intuit compares it exactly, so copy it from here:{' '}
                <code>{redirectUri}</code>
              </p>
            )}

            <form
              className="connection-form"
              onSubmit={(e) => submitCredential(e, { provider: 'qbo-app' })}
            >
              <h3>The Intuit application</h3>
              <p className="muted small">
                One set of app credentials, shared by every company file.{' '}
                {appCredential
                  ? `Stored, last changed ${when(appCredential.lastRefreshedAt)}.`
                  : 'Not stored yet - no company file can be connected until it is.'}
              </p>
              <p className="muted small">
                Intuit issues two key sets per app, and they reach different servers: Development
                keys reach sandbox company files, Production keys reach real books. Nothing in the
                sign-in says which set you hold, so a mismatch shows up as a 403 the first time a
                company file is read. Pick the set these keys came from. Nothing stored here can be
                read back, so changing the key set later means pasting the client id and secret
                again.
              </p>
              <div className="connection-fields">
                <label>
                  Client id
                  <input
                    name="clientId"
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={500}
                    placeholder={appCredential ? 'Stored - paste again to replace' : ''}
                  />
                </label>
                <label>
                  Client secret
                  <input
                    name="clientSecret"
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={2000}
                    placeholder={appCredential ? 'Stored - paste again to replace' : ''}
                  />
                </label>
                <label>
                  Key set
                  <select name="keySet" defaultValue="production">
                    <option value="production">Production - real company files</option>
                    <option value="development">Development - sandbox companies</option>
                  </select>
                </label>
                <button type="submit" disabled={storeCredential.isPending}>
                  {appCredential ? 'Replace' : 'Store'}
                </button>
              </div>
              <CredentialNote note={credentialNote} provider="qbo-app" />
            </form>

            <form
              className="connection-form"
              onSubmit={(e) => submitCredential(e, { provider: 'guesty' })}
            >
              <h3>Guesty</h3>
              <p className="muted small">
                The Open API application&apos;s client id and secret, from Guesty&apos;s
                Integrations page. One account, read-only.{' '}
                {guestyCredential
                  ? `Stored, last changed ${when(guestyCredential.lastRefreshedAt)}.`
                  : 'Not stored yet - reservations, guests and the calendar stay on the mock until it is.'}
              </p>
              <div className="connection-fields">
                <label>
                  Client id
                  <input
                    name="clientId"
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={500}
                    placeholder={guestyCredential ? 'Stored - paste again to replace' : ''}
                  />
                </label>
                <label>
                  Client secret
                  <input
                    name="clientSecret"
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={2000}
                    placeholder={guestyCredential ? 'Stored - paste again to replace' : ''}
                  />
                </label>
                <button type="submit" disabled={storeCredential.isPending}>
                  {guestyCredential ? 'Replace' : 'Store'}
                </button>
              </div>
              <CredentialNote note={credentialNote} provider="guesty" />
            </form>

            <form className="connection-form" onSubmit={submitStripe}>
              <h3>Stripe</h3>
              <p className="muted small">
                One RESTRICTED key per Stripe account, with read on Payouts and Balance transactions
                and nothing else, bound to the entity whose bank receives the payouts. The key is
                checked against the account on every sync.
              </p>
              <div className="connection-fields">
                <label>
                  Entity
                  <select name="entityId" defaultValue="">
                    <option value="" disabled>
                      Choose the entity
                    </option>
                    {(entities.data?.entities ?? []).map((entity) => (
                      <option key={entity.id} value={entity.id}>
                        {entity.name}
                        {entity.stripe_account_id ? ` (${entity.stripe_account_id})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Account id
                  <input name="accountId" maxLength={80} placeholder="acct_…" spellCheck={false} />
                </label>
                <label>
                  Restricted key
                  <input
                    name="apiKey"
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={500}
                  />
                </label>
                <button type="submit" disabled={bindStripe.isPending}>
                  Connect
                </button>
              </div>
            </form>

            <form className="connection-form" onSubmit={submitEntity}>
              <h3>Add an entity</h3>
              <p className="muted small">
                One of the LLCs that owns or operates the homes. Create it here, then use Connect
                QuickBooks on its row to bind it to a company file - the company file is learned
                from Intuit when you approve it, not typed here.
              </p>
              <div className="connection-fields">
                <label>
                  Name
                  <input name="name" maxLength={200} placeholder="Sixgen Carolina" />
                </label>
                <label>
                  Legal name
                  <input name="legalName" maxLength={200} placeholder="Sixgen Carolina LLC" />
                </label>
                <label>
                  Jurisdiction
                  <input name="jurisdiction" maxLength={50} placeholder="KY" />
                </label>
                <button type="submit" disabled={createEntity.isPending}>
                  {createEntity.isPending ? 'Adding...' : 'Add'}
                </button>
              </div>
            </form>

            {credentials.data?.slo && (
              <div className="connection-form">
                <h3>Service levels</h3>
                <p className="muted small">
                  The two numbers the soak is judged on (OPERATIONS.md §4): every connected account
                  synced within {credentials.data.slo.freshnessHours} hours, and fewer than{' '}
                  {Math.round(credentials.data.slo.agentRuns7d.limit * 100)}% of agent runs failing
                  over seven days. Read from what is recorded; nothing here is estimated.
                </p>
                <p className="small">
                  Agent runs, last 7 days: {credentials.data.slo.agentRuns7d.total} run
                  {credentials.data.slo.agentRuns7d.total === 1 ? '' : 's'},{' '}
                  {credentials.data.slo.agentRuns7d.failed} failed (
                  {Math.round(credentials.data.slo.agentRuns7d.failureRate * 1000) / 10}%)
                  {credentials.data.slo.agentRuns7d.failureRate >=
                  credentials.data.slo.agentRuns7d.limit ? (
                    <span className="chip chip-warn"> over the limit</span>
                  ) : null}
                </p>
                {credentials.data.schema && (
                  <p className="small">
                    Schema: {credentials.data.schema.applied} of {credentials.data.schema.total}{' '}
                    migrations applied
                    {credentials.data.schema.pending.length > 0 ? (
                      <span className="chip chip-warn">
                        {' '}
                        {credentials.data.schema.pending.length} pending:{' '}
                        {credentials.data.schema.pending.join(', ')}
                      </span>
                    ) : credentials.data.schema.drifted.length > 0 ? (
                      <span className="chip chip-warn">
                        {' '}
                        changed since applied: {credentials.data.schema.drifted.join(', ')}
                      </span>
                    ) : (
                      <span className="chip"> current</span>
                    )}
                  </p>
                )}
                <p className="small">
                  <button
                    type="button"
                    disabled={syncNow.isPending}
                    onClick={() => syncNow.mutate()}
                  >
                    {syncNow.isPending ? 'Queuing...' : 'Sync now'}
                  </button>{' '}
                  <span className="muted">
                    Runs the daily 05:30 sync now, for every connected account. Read-only; each
                    connector still needs its flag on.
                  </span>
                </p>
                {credentials.data.slo.importFreshness.length === 0 ? (
                  <p className="muted small">No connected account syncs yet.</p>
                ) : (
                  <ul className="small">
                    {credentials.data.slo.importFreshness.map((f) => (
                      <li key={`${f.provider}/${f.accountRef}`}>
                        <code>
                          {f.provider}/{f.accountRef}
                        </code>{' '}
                        {f.lastSyncedAt
                          ? `last synced ${when(f.lastSyncedAt)} (${f.hoursSince} h ago)`
                          : 'never synced'}
                        {f.stale ? <span className="chip chip-warn"> stale</span> : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {entities.isLoading && <p className="muted">Loading...</p>}

            {!entities.isLoading && connections.length === 0 && (
              <p className="empty">
                There are no entities yet, so there is nothing to connect a company file to.
              </p>
            )}

            {connections.length > 0 && (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Entity</th>
                      <th>Realm</th>
                      <th>Stripe</th>
                      <th>Credential</th>
                      <th>Last refreshed</th>
                      <th>Last synced</th>
                      <th>QuickBooks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {connections.map(({ entity, realm, credential }) => (
                      <tr key={entity.id}>
                        <td>
                          {renaming?.id === entity.id ? (
                            <form
                              className="inline-form"
                              onSubmit={(event) => {
                                event.preventDefault();
                                renameEntity.mutate({ id: entity.id, name: renaming.name });
                              }}
                            >
                              <input
                                id={`rename-${entity.id}`}
                                aria-label="New name"
                                value={renaming.name}
                                maxLength={200}
                                onChange={(event) =>
                                  setRenaming({ id: entity.id, name: event.target.value })
                                }
                              />
                              <button type="submit" disabled={renameEntity.isPending}>
                                {renameEntity.isPending ? 'Saving…' : 'Save'}
                              </button>
                              <button type="button" onClick={() => setRenaming(null)}>
                                Cancel
                              </button>
                            </form>
                          ) : (
                            <>
                              {entity.name}{' '}
                              <button
                                type="button"
                                className="link-button"
                                onClick={() => setRenaming({ id: entity.id, name: entity.name })}
                                title="Rename this entity; the legal name and jurisdiction stay"
                              >
                                Rename
                              </button>
                            </>
                          )}
                        </td>
                        <td>{realm ? <code>{realm}</code> : <span className="muted">—</span>}</td>
                        <td>
                          {entity.stripe_account_id ? (
                            <code>{entity.stripe_account_id}</code>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td>{credential ? 'Connected' : 'Not connected'}</td>
                        <td className="muted">{when(credential?.lastRefreshedAt)}</td>
                        <td className="muted">{when(credential?.lastSyncedAt)}</td>
                        <td>
                          <button
                            type="button"
                            disabled={connect.isPending || !appCredential}
                            onClick={() => connect.mutate(entity.id)}
                            title={
                              appCredential
                                ? 'Sign in at Intuit and approve this company file'
                                : 'Store the Intuit application credentials above first'
                            }
                          >
                            {credential ? 'Reconnect QuickBooks' : 'Connect QuickBooks'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/*
              The fallback, and labelled as one. It is the path that puts a live credential on a
              screen and in a clipboard, which is exactly what the Connect button exists to avoid
              — so it is here for the case where the button genuinely cannot work, and nowhere
              near the top of the section. A realm has to be known to paste a token against it,
              which is why rows without one are not offered a field.
            */}
            <details className="connection-form">
              <summary>Paste a refresh token instead (if the button cannot reach Intuit)</summary>
              <p className="muted small">
                Only if Connect cannot be used. A token pasted here has been on a screen and in a
                clipboard before it arrived, which the button avoids entirely. An entity with no
                realm yet cannot be connected this way &mdash; use the button, which learns the
                realm from Intuit.
              </p>
              {connections
                .filter(({ realm }) => realm !== null)
                .map(({ entity, realm, credential }) => (
                  <form
                    key={entity.id}
                    className="connection-fields"
                    onSubmit={(e) =>
                      submitCredential(e, { provider: 'qbo', accountRef: realm as string })
                    }
                  >
                    <label>
                      {entity.name}
                      <input
                        name="refreshToken"
                        type="password"
                        autoComplete="off"
                        spellCheck={false}
                        maxLength={4000}
                        aria-label={`Refresh token for ${entity.name}`}
                        placeholder={credential ? 'Connected - paste again to replace' : ''}
                      />
                    </label>
                    <button type="submit" disabled={storeCredential.isPending}>
                      {credential ? 'Replace' : 'Store'}
                    </button>
                  </form>
                ))}
            </details>
          </>
        )}
      </div>

      <div className="card">
        <h2>People</h2>
        <p className="muted small">
          Who can sign in. Offboarding deactivates a person — their sessions end now and their
          sign-in is refused — and keeps every decision they made under their name; nobody is ever
          deleted. You cannot deactivate yourself.
        </p>
        {users.data && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Roles</th>
                  <th>Last sign-in</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.data.users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      {u.name ?? '—'}
                      <div className="small muted">{u.email}</div>
                    </td>
                    <td className="small">{u.roles.join(', ') || '—'}</td>
                    <td className="small">
                      {u.last_login_at ? u.last_login_at.slice(0, 16) : '—'}
                    </td>
                    <td>
                      <span className={`chip ${u.is_active ? 'chip-ok' : 'chip-attention'}`}>
                        {u.is_active ? 'active' : 'deactivated'}
                      </span>
                    </td>
                    <td className="small">
                      {u.id !== me?.id && (
                        <button
                          type="button"
                          className="link"
                          disabled={setActive.isPending}
                          onClick={() => setActive.mutate({ id: u.id, isActive: !u.is_active })}
                        >
                          {u.is_active ? 'Deactivate' : 'Reactivate'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {setActive.error && <p className="error">{String(setActive.error)}</p>}
      </div>

      <div className="card">
        <h2>Feature flags</h2>
        <p className="muted small">
          Every capability is off by default and turned on deliberately. A flag whose phase has not
          arrived cannot do anything yet even when enabled.
        </p>

        {flags.isLoading && <p className="muted">Loading…</p>}

        {[...byPhase.keys()]
          .sort((a, b) => a - b)
          .map((phase) => (
            <div key={phase} className="flag-group">
              <h3>
                Phase {phase}
                {phase > 1 && <span className="muted small"> — not built yet</span>}
              </h3>
              {(byPhase.get(phase) ?? []).map((flag) => (
                <label key={flag.key} className="switch">
                  <input
                    type="checkbox"
                    checked={flag.enabled}
                    disabled={setFlag.isPending}
                    onChange={(e) =>
                      setFlag.mutate({ key: flag.key, enabled: e.currentTarget.checked })
                    }
                  />
                  <span>
                    <code>{flag.key}</code>
                    <span className="muted small block">{flag.description}</span>
                  </span>
                </label>
              ))}
            </div>
          ))}
      </div>
    </section>
  );
}

/** The last credential form's outcome, under the form it came from; nothing for any other form. */
function CredentialNote({
  note,
  provider,
}: {
  note: { provider: string; ok: boolean; text: string } | null;
  provider: string;
}): ReactNode {
  if (note === null || note.provider !== provider) return null;
  return (
    <div className={note.ok ? 'alert alert-ok' : 'alert'} role="status">
      {note.text}
    </div>
  );
}
